<script lang="ts">
  import type { LeaderboardViewState } from "../../leaderboard/leaderboardAdapter";
  import type { Translate } from "../i18n";
  import type { RunRecord } from "../preferences";
  import LeaderboardBoard from "./LeaderboardBoard.svelte";

  /**
   * The board, reachable at any time rather than only after a crash.
   *
   * It carries the player's own last runs next to the global list, because
   * the board refuses a run with no landings and the personal best only ever
   * shows one number — without the history a bad run leaves no trace at all.
   */
  const {
    leaderboard,
    history,
    t,
    onClose,
    onReload,
  }: {
    leaderboard: LeaderboardViewState;
    history: readonly RunRecord[];
    t: Translate;
    onClose: () => void;
    onReload: () => void;
  } = $props();

  function duration(seconds: number): string {
    const whole = Math.max(0, Math.floor(seconds));
    return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, "0")}`;
  }

  /** Date only: the day is the useful part, the second is noise. */
  function day(iso: string): string {
    const date = new Date(iso);
    return Number.isFinite(date.getTime())
      ? date.toLocaleDateString(undefined, { month: "short", day: "numeric" })
      : "";
  }
</script>

<section class="overlay" aria-label={t("leaderboard.title")}>
  <div class="dialog dialog--board">
    <h2>{t("leaderboard.title")}</h2>

    <h3 class="board__heading">{t("board.title")}</h3>
    <LeaderboardBoard
      entries={leaderboard.entries}
      status={leaderboard.boardStatus}
      {t}
    />

    <h3 class="board__heading">{t("leaderboard.yourRuns")}</h3>
    {#if history.length === 0}
      <p class="board__note">{t("leaderboard.noRuns")}</p>
    {:else}
      <ol class="runs" aria-label={t("leaderboard.yourRuns")}>
        <!-- Keyed on the index as well: two runs cannot really end in the
             same millisecond, but a duplicate key is a hard error, and the
             list is short and never reordered. -->
        {#each history as run, index (`${run.finishedAt}-${index}`)}
          <li class="runs__row">
            <span class="runs__day">{day(run.finishedAt)}</span>
            <span class="runs__score">
              {run.landings}<span class="runs__unit"
                >{t("leaderboard.landingsShort")}</span
              >
            </span>
            <span class="runs__meta">
              {run.departures}<span class="runs__unit"
                >{t("leaderboard.departuresShort")}</span
              >
            </span>
            <span class="runs__meta">{duration(run.durationSeconds)}</span>
          </li>
        {/each}
      </ol>
    {/if}

    <div class="board__actions">
      <button type="button" class="board__secondary" onclick={onReload}>
        {t("leaderboard.reload")}
      </button>
      <button type="button" onclick={onClose}>{t("settings.close")}</button>
    </div>
  </div>
</section>

<style>
  .board__heading {
    margin: 0.6rem 0 0.1rem;
    font-size: 0.78rem;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    opacity: 0.65;
  }

  .board__note {
    font-size: 0.85rem;
    opacity: 0.7;
  }

  .runs {
    display: grid;
    gap: 0.1rem;
    width: 100%;
    max-width: 20rem;
    margin: 0;
    padding: 0;
    list-style: none;
    font-variant-numeric: tabular-nums;
  }

  .runs__row {
    display: grid;
    grid-template-columns: 3.4rem auto minmax(0, 1fr) auto;
    align-items: baseline;
    gap: 0.5rem;
    padding: 0.16rem 0.2rem;
    font-size: 0.9rem;
    text-align: left;
  }

  .runs__row:nth-child(odd) {
    background: rgb(242 246 249 / 0.06);
  }

  .runs__day {
    opacity: 0.6;
  }

  .runs__score {
    font-weight: 700;
  }

  .runs__meta {
    opacity: 0.75;
  }

  .runs__unit {
    margin-left: 0.1rem;
    font-size: 0.72em;
    font-weight: 400;
    opacity: 0.7;
  }

  .board__actions {
    display: flex;
    gap: 0.5rem;
    justify-content: center;
  }

  .board__secondary {
    background: transparent;
    border: 1px solid rgb(242 246 249 / 0.28);
    color: inherit;
  }
</style>
